import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Image as ImageIcon,
  Video,
  Code,
  GripVertical,
  Loader2,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

export const HomepageSectionsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/admin/homepage-sections`);
        if (response.status === 403 || response.status === 401) {
          navigate('/cms');
          return;
        }
        if (response.ok) {
          const data = await response.json();
          setSections(data);
        }
      } catch (error) {
        console.error('Error fetching sections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, [navigate]);

  const handleAddSection = () => {
    setEditingSection({
      section_id: null, // Will be assigned by server
      name: '',
      order: sections.length,
      background_type: 'color', // color, image, video, embed
      background_value: '#0a0a0a',
      overlay_color: 'black', // black or white
      overlay_opacity: 0.5,
      embed_code: '',
      video_url: '',
      enabled: true
    });
  };

  const handleEditSection = (section) => {
    setEditingSection({ ...section });
  };

  const handleSaveSection = async () => {
    if (!editingSection) return;
    
    setSaving(true);
    try {
      let response;
      if (editingSection.section_id) {
        // Update existing
        response = await authFetch(`${API_URL}/api/admin/homepage-sections/${editingSection.section_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingSection)
        });
      } else {
        // Create new
        response = await authFetch(`${API_URL}/api/admin/homepage-sections`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingSection)
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (editingSection.section_id) {
          setSections(sections.map(s => s.section_id === editingSection.section_id ? data : s));
        } else {
          setSections([...sections, data]);
        }
        setEditingSection(null);
      } else {
        alert('Failed to save section');
      }
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Error saving section');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;

    try {
      const response = await authFetch(`${API_URL}/api/admin/homepage-sections/${sectionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSections(sections.filter(s => s.section_id !== sectionId));
      }
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Homepage Sections
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Eye size={16} />
              Preview
            </a>
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to CMS
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl editorial-serif mb-2">Homepage Section Backgrounds</h1>
              <p className="text-muted-foreground">
                Configure background images, videos, or custom embeds for homepage sections
              </p>
            </div>
            <Button onClick={handleAddSection} className="gap-2">
              <Plus size={16} />
              Add Section
            </Button>
          </div>

          {/* Section List */}
          <div className="space-y-4">
            {sections.length === 0 && !editingSection && (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground mb-4">No custom sections configured yet.</p>
                <Button onClick={handleAddSection} variant="outline" className="gap-2">
                  <Plus size={16} />
                  Add Your First Section
                </Button>
              </Card>
            )}

            {sections.map((section) => (
              <Card key={section.section_id} className="p-4">
                <div className="flex items-center gap-4">
                  <GripVertical className="text-muted-foreground cursor-grab" size={20} />
                  
                  {/* Preview */}
                  <div 
                    className="w-24 h-16 rounded overflow-hidden flex-shrink-0 flex items-center justify-center"
                    style={{
                      backgroundColor: section.background_type === 'color' ? section.background_value : '#1a1a1a',
                      backgroundImage: section.background_type === 'image' ? `url(${section.background_value})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {section.background_type === 'video' && <Video className="text-white/50" size={24} />}
                    {section.background_type === 'embed' && <Code className="text-white/50" size={24} />}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-medium">{section.name || 'Unnamed Section'}</h3>
                    <p className="text-sm text-muted-foreground">
                      {section.background_type === 'color' && `Color: ${section.background_value}`}
                      {section.background_type === 'image' && 'Background Image'}
                      {section.background_type === 'video' && 'Video Background'}
                      {section.background_type === 'embed' && 'Custom Embed'}
                      {' • '}
                      Overlay: {section.overlay_color} @ {Math.round((section.overlay_opacity || 0.5) * 100)}%
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEditSection(section)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteSection(section.section_id)}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Edit Form */}
          {editingSection && (
            <Card className="mt-8 p-6">
              <h3 className="text-lg font-medium mb-6">
                {editingSection.section_id ? 'Edit Section' : 'New Section'}
              </h3>

              <div className="space-y-6">
                {/* Section Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">Section Name</label>
                  <input
                    type="text"
                    value={editingSection.name || ''}
                    onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded"
                    placeholder="e.g., Hero, Featured, In Motion"
                  />
                </div>

                {/* Background Type */}
                <div>
                  <label className="block text-sm font-medium mb-1">Background Type</label>
                  <div className="flex gap-2">
                    {['color', 'image', 'video', 'embed'].map((type) => (
                      <button
                        key={type}
                        onClick={() => setEditingSection({ ...editingSection, background_type: type })}
                        className={`px-4 py-2 rounded capitalize ${
                          editingSection.background_type === type
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Value based on type */}
                {editingSection.background_type === 'color' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Background Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={editingSection.background_value || '#0a0a0a'}
                        onChange={(e) => setEditingSection({ ...editingSection, background_value: e.target.value })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editingSection.background_value || ''}
                        onChange={(e) => setEditingSection({ ...editingSection, background_value: e.target.value })}
                        className="flex-1 px-3 py-2 bg-background border border-border rounded"
                        placeholder="#0a0a0a"
                      />
                    </div>
                  </div>
                )}

                {editingSection.background_type === 'image' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Image URL</label>
                    <input
                      type="text"
                      value={editingSection.background_value || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, background_value: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded"
                      placeholder="https://... or Google Drive URL"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use a direct image URL or Google Drive share link
                    </p>
                  </div>
                )}

                {editingSection.background_type === 'video' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Video URL</label>
                    <input
                      type="text"
                      value={editingSection.video_url || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, video_url: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded"
                      placeholder="YouTube or Vimeo URL"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Video will autoplay muted and loop in the background
                    </p>
                  </div>
                )}

                {editingSection.background_type === 'embed' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Embed Code</label>
                    <textarea
                      value={editingSection.embed_code || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, embed_code: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded font-mono text-sm"
                      rows={4}
                      placeholder="<iframe>...</iframe>"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Paste custom HTML/iframe embed code
                    </p>
                  </div>
                )}

                {/* Overlay Settings */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Overlay Color</label>
                    <select
                      value={editingSection.overlay_color || 'black'}
                      onChange={(e) => setEditingSection({ ...editingSection, overlay_color: e.target.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded"
                    >
                      <option value="black">Black</option>
                      <option value="white">White</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Overlay Opacity: {Math.round((editingSection.overlay_opacity || 0.5) * 100)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={(editingSection.overlay_opacity || 0.5) * 100}
                      onChange={(e) => setEditingSection({ ...editingSection, overlay_opacity: parseInt(e.target.value) / 100 })}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium mb-2">Preview</label>
                  <div 
                    className="h-32 rounded overflow-hidden flex items-center justify-center relative"
                    style={{
                      backgroundColor: editingSection.background_type === 'color' ? editingSection.background_value : '#1a1a1a',
                      backgroundImage: editingSection.background_type === 'image' ? `url(${editingSection.background_value})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    <div 
                      className="absolute inset-0"
                      style={{
                        backgroundColor: editingSection.overlay_color === 'white' ? 'rgba(255,255,255,' : 'rgba(0,0,0,',
                        opacity: editingSection.overlay_opacity || 0.5
                      }}
                    />
                    <span className="relative z-10 text-white font-medium">
                      {editingSection.name || 'Section Preview'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <Button variant="ghost" onClick={() => setEditingSection(null)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSection} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save Section
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Info Box */}
          <Card className="mt-8 p-6 bg-blue-500/10 border-blue-500/20">
            <h3 className="font-medium mb-2 text-blue-400">How to Use</h3>
            <ul className="text-sm text-blue-400/80 space-y-1 list-disc list-inside">
              <li>Create sections with unique names matching your homepage sections</li>
              <li>Choose between solid color, image, video, or custom embed backgrounds</li>
              <li>Adjust overlay opacity to ensure text readability</li>
              <li>Changes will apply to the homepage automatically</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomepageSectionsPage;
